import React, { ComponentProps, memo, useCallback, useRef } from 'react'
import {
  Controller,
  ControllerProps,
  FieldErrors,
  FieldValues,
  get,
} from 'react-hook-form'

import { SelectLegacy } from '@surglogs-app/ui'

import { DefaultFormField, DefaultFormFieldProps } from './DefaultFormField'

interface SelectFieldProps<TValuesModel extends FieldValues>
  extends ComponentProps<typeof SelectLegacy>,
    Pick<
      ControllerProps<TValuesModel>,
      'shouldUnregister' | 'control' | 'name' | 'defaultValue'
    > {
  errors?: FieldErrors<any>
  warnings?: Record<string, any>
  formFieldProps?: Omit<
    DefaultFormFieldProps,
    'name' | 'errors' | 'warnings' | 'children'
  >
}

export const SELECT_NON_EXISTING_VALUE_ID = -1

const Component = <TValuesModel extends FieldValues = FieldValues>({
  control,
  defaultValue,
  errors,
  warnings,
  name,
  invalid,
  shouldUnregister,
  warning,
  onChange: handleChange,
  ...rest
}: SelectFieldProps<TValuesModel>) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const onFocus = useCallback(() => inputRef.current?.focus(), [])

  return (
    <Controller<TValuesModel>
      {...{
        defaultValue,
        control,
        name,
        onFocus,
        shouldUnregister,
        render: ({ field: { onBlur, onChange, value } }) => (
          <SelectLegacy
            {...rest}
            {...{
              onBlur,
              onChange: handleChange
                ? (...args) => {
                    handleChange?.(...args)
                    return onChange(...args)
                  }
                : onChange,
              invalid: invalid ?? get(errors || {}, name),
              warning: warning ?? get(warnings || {}, name),
              ref: inputRef,
              value,
            }}
          />
        ),
      }}
    />
  )
}

/** @deprecated */
export const SelectFieldLegacy = memo(
  <TValuesModel extends FieldValues = FieldValues>({
    formFieldProps = undefined,
    ...rest
  }: SelectFieldProps<TValuesModel>) => {
    if (formFieldProps) {
      return (
        <DefaultFormField
          {...{
            ...formFieldProps,
            name: rest.name,
            errors: rest.errors,
            warnings: rest.warnings,
            invalid: rest.invalid,
          }}>
          <Component {...rest} />
        </DefaultFormField>
      )
    }

    return <Component {...rest} />
  },
) as typeof Component
